import React from 'react'
import * as R from 'remeda'

import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { useSurveyQueryCustomerAttributeGroups } from '~/hooks/useSurveyQuery'
import { exists, QuestionOp } from '~/utils'

import { useGroupedQuestions } from '../hooks/useGroupedQuestions'
import { useOmitNonTabulatedGroupsDiffs } from '../hooks/useOmitNonTabulatedGroupsDiffs'
import { useOmitNonTabulatedQuestionsDiffs } from '../hooks/useOmitNonTabulatedQuestionsDiffs'
import { useTabulatedGroupsAndAll } from '../hooks/useTabulatedGroupsAndAll'
import { useAtomValueContext } from '../providers/AtomsProvider'

import { EditGroupNameDialog } from './EditGroupNameDialog'
import { IndividualCustomerAttributeChart } from './IndividualCustomerAttributeChart'
import { IndividualCustomerAttributeTable } from './IndividualCustomerAttributeTable'
import { IndividualQuestionChart } from './IndividualQuestionChart'
import { IndividualQuestionTable } from './IndividualQuestionTable'
import { IndividualTableChartContainer } from './IndividualTableChartContainer'
import { MatrixTable } from './MatrixTable'
import { SequentialTable } from './SequentialTable'

export const GroupCountsFiguresContent = () => {
  const [normalQuestions, matrixQuestions] = useGroupedQuestions()
  const customerAttributeQueryData = useAtomValueContext(
    'customerAttributeQueryData',
  )
  const allCustomerAttributeGroups = useSurveyQueryCustomerAttributeGroups()
  const customerAttributeGroups = React.useMemo(() => {
    const customerAttributeIds = customerAttributeQueryData?.map(
      R.prop('customerAttributeId'),
    )
    if (!exists(customerAttributeIds)) {
      return []
    }

    return allCustomerAttributeGroups.filter((allCustomerAttributeGroup) =>
      allCustomerAttributeGroup.customerAttributes.some((customerAttribute) =>
        customerAttributeIds.includes(customerAttribute.id),
      ),
    )
  }, [allCustomerAttributeGroups, customerAttributeQueryData])

  const resultViewerType = useAtomValueContext('resultViewerType')
  const tabulatedGroupsAndAll = useTabulatedGroupsAndAll()
  const nameEditingGroup = useAtomValueContext('nameEditingGroup')

  useOmitNonTabulatedGroupsDiffs()
  useOmitNonTabulatedQuestionsDiffs()

  return (
    <VStack spacing={8}>
      {(0 < normalQuestions.length || 0 < customerAttributeGroups.length) &&
      resultViewerType === 'sequential' ? (
        <SequentialTable />
      ) : (
        <VStack spacing={8}>
          {normalQuestions.map((question) => (
            <IndividualTableChartContainer
              key={question.question_id}
              id={question.question_id}
              label={QuestionOp.toText(question)}
            >
              <IndividualQuestionTable question={question} />

              <IndividualQuestionChart question={question} />
            </IndividualTableChartContainer>
          ))}

          {customerAttributeGroups.map((customerAttributeGroup) => (
            <IndividualTableChartContainer
              key={customerAttributeGroup.id}
              id={customerAttributeGroup.id}
              label={customerAttributeGroup.name}
            >
              <IndividualCustomerAttributeTable
                customerAttributeGroup={customerAttributeGroup}
              />

              <IndividualCustomerAttributeChart
                customerAttributeGroup={customerAttributeGroup}
              />
            </IndividualTableChartContainer>
          ))}
        </VStack>
      )}

      {0 < matrixQuestions.length && (
        <VStack spacing={8}>
          {matrixQuestions.map((question) => (
            <VStack
              key={question.question_id}
              id={`matrix-${question.question_id}-all`}
              spacing={1}
            >
              <Typography fontWeight={700} lineHeight={2.5} variant="normal">
                {QuestionOp.toText(question)}
              </Typography>

              <VStack align="start" spacing={4}>
                {tabulatedGroupsAndAll.map((group) => (
                  <MatrixTable key={group} group={group} question={question} />
                ))}
              </VStack>
            </VStack>
          ))}
        </VStack>
      )}

      {exists(nameEditingGroup) && <EditGroupNameDialog />}
    </VStack>
  )
}
